import type { ArticleContent } from "./types";

export const articleEs: Record<string, ArticleContent> = {
  "basetao-spreadsheet-guide": {
    slug: "basetao-spreadsheet-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20workspace%20flat%20lay%20with%20a%20laptop%20showing%20a%20colorful%20spreadsheet%20grid%2C%20a%20notebook%2C%20a%20coffee%20cup%20and%20a%20folded%20sage%20green%20sweater%20on%20a%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20airy%20editorial%20photography%2C%20fresh%20minimal%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-guide&orientation=landscape&nocache=true",
    title: "Basetao Spreadsheet: guía completa de productos, vendedores y compras (2026)",
    cardDescription: "Cómo funciona la hoja de cálculo de Basetao, cómo se organizan las categorías y cómo encontrar vendedores fiables.",
    metaDescription: "Aprende cómo funciona la hoja de cálculo de Basetao, explora categorías, encuentra vendedores fiables y entiende el envío y el control de calidad.",
    eyebrow: "Guía de compra",
    intro: "La hoja de cálculo de Basetao es un directorio compartido que reúne enlaces de productos, datos de vendedores y notas de compra en un solo lugar. Esta guía explica su organización, su lectura y su uso para comprar con confianza.",
    sections: [
      {
        heading: "¿Qué es la hoja de cálculo de Basetao?",
        paragraphs: [
          "La hoja de cálculo de Basetao es una lista seleccionada y organizada de productos y vendedores que usan quienes compran en marketplaces internacionales. En lugar de navegar sin fin, se abre para encontrar enlaces directos, atajos de categorías y notas de vendedores en una sola vista.",
          "Actúa como puente entre una búsqueda y una compra real. Cada entrada suele apuntar a una página de producto activa en el sitio principal.",
        ],
      },
      {
        heading: "Cómo se organizan las categorías",
        paragraphs: [
          "La hoja agrupa productos en categorías claras como Zapatos, Sudaderas, Camisetas, Chaquetas, Pantalones y Shorts, Gorras, Conjuntos, Ropa interior, Camisetas deportivas, Accesorios, Bolsos, Cinturones y Perfumes.",
          "Cada categoría enlaza a una vista filtrada en el sitio principal, para pasar de una idea general a una lista concreta en un clic.",
        ],
      },
      {
        heading: "Cómo encontrar vendedores fiables",
        paragraphs: [
          "Busca vendedores con fichas completas, notas de talla claras y fotos realistas. Las entradas fiables suelen incluir un enlace a la página del producto, una nota de talla o material y una fecha de actualización.",
          "Si una ficha es vaga o sin detalles, trátala con cautela. Las entradas más útiles te ayudan a verificar un producto antes de comprometerte.",
        ],
      },
      {
        heading: "Conceptos básicos de envío y control de calidad",
        paragraphs: [
          "Antes de comprar, revisa la guía de tallas, la descripción del material y las reseñas disponibles. En ropa, compara las medidas indicadas con las tuyas.",
          "Los tiempos y costes de envío varían según el artículo y el destino. Confirma siempre el precio final, el plazo estimado y la política de devolución antes de pedir.",
        ],
      },
    ],
    comparisonTitle: "Referencia rápida de categorías",
    comparisonHeaders: ["Categoría", "Qué revisar", "Ideal para"],
    comparisonRows: [
      ["Zapatos", "Talla, suela, colores", "Diario y streetwear"],
      ["Sudaderas", "Material, peso, ajuste", "Looks casuales y por capas"],
      ["Camisetas", "Tejido, calidad de estampado, ajuste", "Básicos y gráficos"],
      ["Chaquetas", "Aislamiento, impermeabilidad, talla", "Abrigo y temporadas"],
      ["Accesorios y bolsos", "Material, correa, capacidad", "Toques finales"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿La hoja de cálculo de Basetao es gratis?", a: "Sí. Es un directorio informativo y las páginas enlazadas están en el sitio principal." },
      { q: "¿Necesito una cuenta para usarla?", a: "No. Explorar categorías y leer guías no requiere cuenta. Las compras se gestionan en el sitio principal." },
      { q: "¿Con qué frecuencia se actualiza?", a: "Las entradas se revisan regularmente, pero la disponibilidad y los precios pueden cambiar. Verifica siempre en la página activa." },
      { q: "¿Este sitio está afiliado a los vendedores?", a: "Es un recurso informativo independiente que organiza enlaces públicos sin sustituir las políticas del sitio principal." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-review": {
    slug: "basetao-review",
    image:
      "https://readdy.ai/api/search-image?query=A%20modern%20desk%20scene%20with%20a%20tablet%20displaying%20a%20product%20listing%20and%20a%20small%20star%20rating%20graphic%20printed%20on%20paper%20beside%20a%20coffee%20cup%2C%20warm%20terracotta%20accents%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-review&orientation=landscape",
    title: "Reseña de Basetao: envío, almacén, control de calidad y seguridad",
    cardDescription: "Una mirada honesta al envío, almacén, control de calidad y seguridad de Basetao — qué funciona y qué vigilar.",
    metaDescription: "Una reseña honesta de Basetao sobre opciones de envío, gestión del almacén, control de calidad y seguridad del comprador.",
    eyebrow: "Reseña",
    intro: "Esta reseña repasa los aspectos de Basetao que más importan — envío, almacén, control de calidad y seguridad — para que decidas si encaja con tu forma de comprar.",
    sections: [
      {
        heading: "Envío: opciones y qué esperar",
        paragraphs: [
          "Basetao ofrece varios métodos de envío que varían según destino, rapidez y coste. Los servicios exprés cuestan más pero incluyen seguimiento detallado, mientras que las opciones económicas son más baratas y lentas.",
          "La mejor elección depende de la urgencia y del presupuesto. Revisa siempre el plazo estimado antes de finalizar el pedido.",
        ],
      },
      {
        heading: "Almacén y gestión del pedido",
        paragraphs: [
          "El almacén es donde tus artículos se reciben, revisan y preparan para el envío. Un almacén bien gestionado mantiene tu pedido junto y confirma que lo comprado coincide con lo enviado.",
          "Las actualizaciones claras reducen sorpresas. Si un artículo llega dañado o distinto, las notas y fotos del almacén sirven de base para un reembolso o cambio.",
        ],
      },
      {
        heading: "Control de calidad y seguridad",
        paragraphs: [
          "El control de calidad ayuda a detectar problemas evidentes antes del envío, pero no es garantía de perfección. Las fotos y listas son útiles, aunque pequeños defectos pueden pasar.",
          "Por seguridad, paga con métodos reconocidos, guarda los registros del pedido y verifica los datos del vendedor antes de compras grandes.",
        ],
      },
    ],
    comparisonTitle: "Basetao de un vistazo",
    comparisonHeaders: ["Área", "Qué funciona bien", "Qué vigilar"],
    comparisonRows: [
      ["Envío", "Varios métodos y seguimiento", "Coste y tiempo según método"],
      ["Almacén", "Consolidación y revisiones", "Depende del estado al llegar"],
      ["Control de calidad", "Fotos y listas de control", "No es garantía de perfección"],
      ["Seguridad", "Métodos de pago reconocidos", "Verifica siempre a los vendedores"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Basetao es un servicio fiable?", a: "Muchos compradores lo usan con éxito, pero como cualquier marketplace depende de los vendedores y artículos. Investigar reduce el riesgo." },
      { q: "¿Basetao inspecciona cada artículo?", a: "Las revisiones y el control de calidad ayudan, pero no garantizan que cada artículo sea perfecto." },
      { q: "¿Puedo rastrear mi pedido?", a: "La mayoría de los métodos incluyen seguimiento. Elige un servicio rastreable para seguir tu paquete hasta la puerta." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "is-basetao-legit": {
    slug: "is-basetao-legit",
    image:
      "https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20over%20a%20printed%20document%20on%20a%20clean%20desk%20with%20a%20small%20padlock%20and%20a%20pen%2C%20soft%20peach%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-legit&orientation=landscape",
    title: "¿Basetao es legítimo? Seguridad, control de calidad y guía de compra",
    cardDescription: "Una respuesta clara sobre si Basetao es legítimo, más los pasos de seguridad y control de calidad que protegen tu dinero.",
    metaDescription: "¿Basetao es legítimo? Analizamos seguridad, control de calidad y protección del comprador para que compres con confianza.",
    eyebrow: "Verificación de seguridad",
    intro: "Una pregunta frecuente es si Basetao es legítimo. La respuesta corta es que es un servicio real muy usado, pero tu seguridad depende de cómo compres.",
    sections: [
      {
        heading: "Qué hace legítimo a un servicio",
        paragraphs: [
          "Un servicio legítimo tiene políticas claras, soporte real y opciones de envío y pago transparentes. Basetao las ofrece, pero la calidad de vendedores y fichas varía.",
          "La legitimidad de la plataforma no hace automáticamente confiable cada producto o vendedor. Trata cada compra como una decisión propia.",
        ],
      },
      {
        heading: "Cómo proteger tu dinero",
        paragraphs: [
          "Usa métodos de pago reconocidos con protección al comprador y guarda cada pedido, mensaje y recibo. Evita pagar fuera de la plataforma o por vías no rastreables.",
          "Si una oferta parece demasiado buena, suele serlo. Compara precios y lee la descripción antes de pagar.",
        ],
      },
      {
        heading: "El papel del control de calidad",
        paragraphs: [
          "El control de calidad es una capa útil que revisa los artículos en busca de problemas evidentes antes del envío. Reduce — sin eliminar — el riesgo de recibir un producto erróneo o dañado.",
          "Pide fotos de control de calidad cuando estén disponibles y notifica cualquier discrepancia al recibir el pedido.",
        ],
      },
    ],
    comparisonTitle: "Lista de verificación de legitimidad",
    comparisonHeaders: ["Revisión", "Por qué importa", "Qué hacer"],
    comparisonRows: [
      ["Método de pago", "Protección al comprador", "Usar opciones reconocidas"],
      ["Datos del vendedor", "Confianza y responsabilidad", "Leer fichas y reseñas"],
      ["Fotos de control", "Detecta problemas visibles", "Pedirlas si es posible"],
      ["Registros del pedido", "Soporte en disputas", "Guardar recibos y mensajes"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Basetao es una estafa?", a: "No. Basetao es una plataforma legítima, pero debes investigar a los vendedores y productos antes de comprar." },
      { q: "¿Qué pasa si mi pedido nunca llega?", a: "Contacta con soporte con los datos y el seguimiento. Los métodos de pago reconocidos suelen ofrecer protección adicional." },
      { q: "¿Debo pedir fotos de control de calidad?", a: "Sí. Ayudan a detectar problemas evidentes antes del envío y sirven de prueba si algo falla." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-shipping-cost": {
    slug: "basetao-shipping-cost",
    image:
      "https://readdy.ai/api/search-image?query=A%20stack%20of%20cardboard%20shipping%20boxes%20with%20a%20shipping%20label%20and%20a%20measuring%20tape%20on%20a%20warm%20sand%20table%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-cost&orientation=landscape",
    title: "Coste de envío de Basetao: guía completa y comparación",
    cardDescription: "Cómo se calculan los costes de envío de Basetao, los métodos disponibles y cómo elegir la opción fiable más barata.",
    metaDescription: "Una guía completa de los costes de envío de Basetao — cómo se calculan, los métodos disponibles y cómo elegir la opción más barata.",
    eyebrow: "Envío",
    intro: "El coste de envío es una de las primeras cosas que los compradores quieren entender. Los precios de Basetao dependen del tamaño y peso del artículo, del destino y del método elegido.",
    sections: [
      {
        heading: "Cómo se calculan los costes de envío",
        paragraphs: [
          "La mayoría de los precios se basan en el peso real o volumétrico del paquete y la distancia. Los artículos más grandes y pesados cuestan más de enviar.",
          "El coste final suele mostrarse antes de pagar, pero conviene comprobar si los impuestos o tasas están incluidos.",
        ],
      },
      {
        heading: "Comparar métodos de envío",
        paragraphs: [
          "Los métodos económicos son los más baratos pero lentos; los exprés son más rápidos y caros. Los estándar equilibran coste y tiempo.",
          "Elegir el método correcto es un equilibrio entre presupuesto y rapidez. Si no hay prisa, el envío económico ahorra una cantidad apreciable.",
        ],
      },
      {
        heading: "Cómo reducir costes",
        paragraphs: [
          "Consolidar varios artículos en un envío suele reducir el coste por artículo. Esperar a un pedido mayor hace que el envío económico valga más la pena.",
          "Compara el coste total — precio más envío — en lugar de solo la tarifa de envío. Un artículo algo más caro con envío barato puede ser mejor trato.",
        ],
      },
    ],
    comparisonTitle: "Comparación de métodos de envío",
    comparisonHeaders: ["Método", "Coste", "Rapidez", "Ideal para"],
    comparisonRows: [
      ["Económico", "El más bajo", "El más lento", "Presupuesto, sin prisa"],
      ["Estándar", "Moderado", "Equilibrado", "La mayoría de pedidos"],
      ["Exprés", "El más alto", "El más rápido", "Artículos urgentes o valiosos"],
      ["Consolidado", "Más barato por artículo", "Variable", "Varios artículos a la vez"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Por qué varían tanto los costes de envío?", a: "El coste depende del peso y tamaño del paquete, el país de destino y el método elegido." },
      { q: "¿Es más barato enviar varios artículos juntos?", a: "Normalmente sí. Consolidar en un paquete suele reducir el coste por artículo." },
      { q: "¿Los impuestos están incluidos en el envío?", a: "Varía. Revisa siempre el desglose del precio final para ver si hay impuestos o tasas." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-shipping-guide": {
    slug: "basetao-shipping-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20cardboard%20parcel%20with%20a%20shipping%20label%20next%20to%20a%20delivery%20van%20model%20and%20a%20clipboard%20on%20a%20soft%20olive%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-guide&orientation=landscape",
    title: "Guía de envío de Basetao: métodos, plazos y seguimiento",
    cardDescription: "Una guía práctica de los métodos de envío, plazos de entrega y seguimiento de Basetao para saber exactamente qué esperar.",
    metaDescription: "Entiende los métodos de envío, plazos y seguimiento de Basetao. Aprende a elegir el servicio correcto y a seguir tu paquete.",
    eyebrow: "Guía de envío",
    intro: "Entender el envío antes de pedir elimina gran parte del estrés de la espera. Esta guía cubre los métodos principales, plazos típicos y cómo seguir tu paquete.",
    sections: [
      {
        heading: "Métodos de envío explicados",
        paragraphs: [
          "Basetao envía mediante una mezcla de servicios económicos, estándar y exprés. Cada uno equilibra coste, rapidez y nivel de seguimiento.",
          "Tu elección debe reflejar la urgencia y cuánto quieres ahorrar. Para la mayoría, un servicio estándar con seguimiento es un buen punto medio.",
        ],
      },
      {
        heading: "Plazos de entrega por método",
        paragraphs: [
          "Los plazos dependen del método y el destino. El envío económico puede tardar semanas; el exprés puede llegar en días.",
          "Trata las estimaciones como una guía, no una promesa. Aduanas, festivos y clima pueden añadir tiempo.",
        ],
      },
      {
        heading: "Cómo funciona el seguimiento",
        paragraphs: [
          "Un número de seguimiento te permite seguir tu paquete del almacén a tu puerta. La mayoría de servicios actualizan el estado en puntos clave.",
          "Si el seguimiento se detiene, espera un par de días antes de contactar, pues los escaneos internacionales a veces van con retraso.",
        ],
      },
    ],
    comparisonTitle: "Resumen de entrega",
    comparisonHeaders: ["Método", "Tiempo típico", "Seguimiento", "Bueno saber"],
    comparisonRows: [
      ["Económico", "2–6 semanas", "Limitado", "Opción más barata"],
      ["Estándar", "1–3 semanas", "Bueno", "Elección equilibrada"],
      ["Exprés", "3–7 días", "Detallado", "Más rápido y caro"],
      ["Mensajería", "Variable", "Puerta a puerta", "A menudo lo más fiable"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Cuánto tarda el envío de Basetao?", a: "Depende del método y destino, de unos días en exprés a varias semanas en económico." },
      { q: "¿Recibiré un número de seguimiento?", a: "La mayoría de métodos estándar y exprés incluyen seguimiento. Los económicos pueden ofrecerlo limitado." },
      { q: "¿Qué hago si mi paquete se retrasa?", a: "Revisa primero el estado, luego contacta con soporte si no hay actualización durante mucho tiempo." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-coupon-discount-code": {
    slug: "basetao-coupon-discount-code",
    image:
      "https://readdy.ai/api/search-image?query=A%20pair%20of%20scissors%20and%20a%20discount%20gift%20tag%20with%20a%20receipt%20and%20a%20small%20stack%20of%20coins%20on%20a%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-coupon&orientation=landscape",
    title: "Cupón y código de descuento de Basetao: guía actualizada",
    cardDescription: "Dónde encontrar cupones y códigos de descuento de Basetao, cómo usarlos y cómo evitar ofertas caducadas o inválidas.",
    metaDescription: "Encuentra y usa cupones y códigos de descuento de Basetao. Aprende de dónde vienen, cómo aplicarlos y evitar ofertas caducadas.",
    eyebrow: "Ahorro",
    intro: "Los cupones y códigos de descuento son una forma sencilla de reducir el coste total de un pedido. Esta guía explica su origen, uso y errores comunes.",
    sections: [
      {
        heading: "De dónde vienen los cupones y códigos",
        paragraphs: [
          "Los códigos se comparten a menudo por boletines, promociones de temporada y el sitio principal. Algunos valen para todo el sitio, otros para categorías o mínimos concretos.",
          "Los códigos más fiables vienen de fuentes oficiales. Los de sitios de terceros deben revisarse, pues pueden estar caducados o ser inválidos.",
        ],
      },
      {
        heading: "Cómo aplicar un código de descuento",
        paragraphs: [
          "La mayoría de códigos se introducen en el pago, en un campo dedicado, antes de confirmar. El descuento se resta del total antes de pagar.",
          "Lee primero las condiciones, pues muchos códigos exigen un mínimo o excluyen categorías. Aplicarlo mal puede hacerlo parecer caducado.",
        ],
      },
      {
        heading: "Evitar ofertas caducadas o inválidas",
        paragraphs: [
          "Revisa siempre la fecha de caducidad y el mínimo antes de armar tu carrito en torno a un código. Un código caducado la semana pasada no funciona esta semana.",
          "Si un código no aplica, revisa la ortografía y las condiciones antes de asumir que es falso. Algunos descuentos distinguen mayúsculas o son para nuevos clientes.",
        ],
      },
    ],
    comparisonTitle: "Tipos de descuento de un vistazo",
    comparisonHeaders: ["Tipo", "Cómo funciona", "Requisito típico", "Dónde encontrarlo"],
    comparisonRows: [
      ["Porcentaje", "Reduce el total un porcentaje", "A menudo un mínimo", "Boletines y promos"],
      ["Importe fijo", "Resta una suma del total", "Valor mínimo", "Campañas de temporada"],
      ["Envío gratis", "Elimina la tarifa de envío", "Puede exigir un mínimo", "Banners del sitio"],
      ["Oferta de categoría", "Aplica a una categoría", "Específico de categoría", "Páginas de producto"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Dónde encuentro un código de descuento de Basetao?", a: "Revisa el sitio principal, boletines y promociones de temporada. Las fuentes oficiales son las más fiables." },
      { q: "¿Por qué no funciona mi código?", a: "Puede estar caducado, exigir un mínimo o estar limitado a categorías o nuevos clientes." },
      { q: "¿Puedo combinar varios cupones?", a: "Normalmente no. La mayoría de tiendas permiten un solo código por pedido; elige el que más ahorre." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-warehouse-guide": {
    slug: "basetao-warehouse-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20bright%20warehouse%20aisle%20with%20neatly%20stacked%20cardboard%20boxes%20on%20steel%20shelves%20and%20a%20soft%20stone%20color%20palette%2C%20natural%20overhead%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-warehouse&orientation=landscape",
    title: "Guía del almacén de Basetao: cómo funciona el almacenamiento",
    cardDescription: "Cómo el almacén de Basetao recibe, almacena y consolida tus artículos antes de enviártelos.",
    metaDescription: "Descubre cómo el almacén de Basetao recibe, almacena y consolida tus artículos, y cómo afecta al tiempo y la precisión del envío.",
    eyebrow: "Almacén",
    intro: "El almacén es donde tus artículos se reciben, guardan y preparan para el envío. Entender esta etapa ayuda a decidir mejor sobre consolidación y entrega.",
    sections: [
      {
        heading: "Cómo recibe artículos el almacén",
        paragraphs: [
          "Cuando un vendedor envía un artículo, llega al almacén y se registra contra tu pedido. Esto confirma que llegó el artículo correcto antes de guardarlo.",
          "Los registros de recepción crean un rastro. Si algo falta o está mal, el registro del almacén es el primer lugar a mirar.",
        ],
      },
      {
        heading: "Almacenamiento y consolidación",
        paragraphs: [
          "Los artículos se guardan hasta que quieras enviarlos. Si compras a varios vendedores, el almacén puede consolidar todo en un paquete, lo que suele bajar el coste.",
          "La consolidación es valiosa en pedidos internacionales, donde enviar varios paquetes pequeños por separado costaría mucho más que una caja.",
        ],
      },
      {
        heading: "Preparar tu pedido para el envío",
        paragraphs: [
          "Antes de enviar, el personal confirma el contenido y prepara el paquete. Las fotos y listas en esta etapa ayudan a detectar problemas evidentes pronto.",
          "Un paquete bien preparado llega menos dañado. Pregunta por el embalaje y las fotos si envías artículos frágiles o valiosos.",
        ],
      },
    ],
    comparisonTitle: "Flujo del almacén",
    comparisonHeaders: ["Etapa", "Qué ocurre", "Por qué importa"],
    comparisonRows: [
      ["Recepción", "Artículos registrados", "Confirma el artículo correcto"],
      ["Almacenamiento", "Artículos guardados", "Permite esperar y combinar"],
      ["Consolidación", "Varios artículos unidos", "Baja el coste por artículo"],
      ["Embalaje", "Contenido revisado y embalado", "Reduce daños y errores"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Cuánto tiempo permanecen los artículos en el almacén?", a: "Los plazos varían; consulta la política actual para evitar tasas o devoluciones automáticas." },
      { q: "¿Qué es la consolidación?", a: "Une artículos de varios vendedores en un paquete, lo que suele reducir el coste total de envío." },
      { q: "¿El almacén inspecciona mis artículos?", a: "Las revisiones básicas son comunes, pero el control de calidad detallado suele ser un paso aparte que puedes pedir." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-qc-guide": {
    slug: "basetao-qc-guide",
    image:
      "https://readdy.ai/api/search-image?query=Two%20hands%20inspecting%20a%20folded%20garment%20with%20a%20checklist%20clipboard%20and%20a%20measuring%20tape%20on%20a%20clean%20desk%2C%20soft%20sage%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-qc&orientation=landscape",
    title: "Guía de control de calidad de Basetao: el QC explicado",
    cardDescription: "Qué revisa realmente el control de calidad de Basetao, cómo pedir fotos de QC y qué puede y no puede garantizar el proceso.",
    metaDescription: "Entiende el control de calidad de Basetao — qué revisa, cómo pedir fotos de QC y qué puede y no puede garantizar.",
    eyebrow: "Control de calidad",
    intro: "El control de calidad, o QC, es el paso donde tus artículos se inspeccionan en busca de problemas evidentes antes del envío. Es una de las herramientas más útiles para comprar con confianza.",
    sections: [
      {
        heading: "Qué revisa el control de calidad",
        paragraphs: [
          "El QC suele comprobar que el artículo coincide con la ficha, se ve correcto y no tiene daños evidentes. En ropa, puede incluir talla, color y costuras básicas.",
          "El objetivo es detectar problemas claros antes de que el artículo viaje por el mundo. No es una prueba profunda, pero filtra muchos problemas comunes.",
        ],
      },
      {
        heading: "Cómo pedir fotos de QC",
        paragraphs: [
          "Muchos servicios permiten pedir fotos de tus artículos antes del envío. Te muestran exactamente qué recibirás y confirman la coincidencia con la ficha.",
          "Revisa las fotos con cuidado. Si algo se ve mal — color equivocado, defecto visible o estilo distinto — es mucho más fácil corregirlo antes del envío.",
        ],
      },
      {
        heading: "Qué puede y no puede hacer el QC",
        paragraphs: [
          "El QC detecta problemas visibles, pero no puede probar durabilidad, tacto del tejido ni calidad a largo plazo. Pequeños defectos ocultos pueden pasar una revisión visual.",
          "Usa el QC como parte de tu decisión, no como garantía total. Combínalo con una lectura atenta y una buena elección de vendedor.",
        ],
      },
    ],
    comparisonTitle: "Resumen del proceso de QC",
    comparisonHeaders: ["Paso", "Qué revisa", "Limitación"],
    comparisonRows: [
      ["Coincidencia", "Artículo vs. ficha", "No revisa detalles ocultos"],
      ["Inspección visual", "Daños o defectos evidentes", "Pequeños fallos posibles"],
      ["Talla / color", "Especificaciones básicas", "El ajuste depende de ti"],
      ["Fotos", "Lo que recibirás", "No es prueba de durabilidad"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Vale la pena el control de calidad?", a: "Sí, sobre todo en ropa y artículos de valor. Detecta muchos problemas evidentes antes del envío." },
      { q: "¿El QC garantiza un artículo perfecto?", a: "No. Detecta problemas visibles pero no puede probar durabilidad, tacto ni calidad a largo plazo." },
      { q: "¿Cómo consigo fotos de QC?", a: "Pídelas a través del servicio al hacer o revisar tu pedido, si la opción está disponible." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-safety-guide": {
    slug: "basetao-safety-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20padlock%20and%20a%20shield-shaped%20wooden%20token%20on%20a%20clean%20desk%20with%20a%20notebook%20and%20pen%2C%20soft%20warm%20gray%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-safety&orientation=landscape",
    title: "Guía de seguridad y prevención de estafas de Basetao",
    cardDescription: "Pasos prácticos para mantenerte seguro en Basetao — pagos seguros, detectar vendedores sospechosos y proteger tu cuenta.",
    metaDescription: "Mantente seguro en Basetao con consejos antiestafa: pagos seguros, detección de vendedores sospechosos y protección de tu cuenta.",
    eyebrow: "Seguridad",
    intro: "Comprar seguro es sobre todo cuestión de buenos hábitos. Esta guía cubre pagos seguros, cómo detectar vendedores sospechosos y proteger tu cuenta y tu dinero.",
    sections: [
      {
        heading: "Usa métodos de pago seguros",
        paragraphs: [
          "Paga siempre con métodos reconocidos y rastreables que ofrezcan protección al comprador. Evita pagar directamente a vendedores o por servicios no rastreables.",
          "Si alguien te pide pagar fuera de la plataforma o por un método inusual, trátalo como una señal de alerta y aléjate.",
        ],
      },
      {
        heading: "Detectar vendedores sospechosos",
        paragraphs: [
          "Los vendedores sospechosos suelen tener fichas vagas, precios irreales o te presionan para actuar rápido. Un precio muy por debajo del mercado rara vez es una ganga real.",
          "Revisa los datos del vendedor, lee la descripción completa y busca información coherente. Los vendedores fiables dan fichas claras y concretas.",
        ],
      },
      {
        heading: "Proteger tu cuenta",
        paragraphs: [
          "Usa una contraseña fuerte y única y activa las funciones de seguridad disponibles. Nunca compartas tus credenciales ni códigos de un solo uso, ni con quien diga ser soporte.",
          "Guarda pedidos, mensajes y pagos. Una buena documentación facilita mucho resolver una disputa si algo sale mal.",
        ],
      },
    ],
    comparisonTitle: "Lista de verificación de seguridad",
    comparisonHeaders: ["Hábito", "Por qué ayuda", "Señal de alerta a evitar"],
    comparisonRows: [
      ["Pagos reconocidos", "Protección al comprador", "Transferencias directas o no rastreables"],
      ["Verificar al vendedor", "Responsabilidad", "Fichas vagas o ausentes"],
      ["Leer descripciones", "Evitar sorpresas", "Precios irreales"],
      ["Guardar registros", "Disputas más fáciles", "Borrar el historial pronto"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Cuál es la forma más segura de pagar en Basetao?", a: "Usa métodos de pago reconocidos y rastreables con protección al comprador. Evita transferencias directas." },
      { q: "¿Cómo detecto a un vendedor sospechoso?", a: "Busca fichas vagas, precios irreales y presión para actuar rápido. Los fiables dan detalles claros." },
      { q: "¿Qué hago si sospecho una estafa?", a: "Detén la transacción, denuncia al vendedor por la plataforma y contacta con soporte con tus registros." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-buying-guide": {
    slug: "basetao-buying-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20paper%20shopping%20bag%20with%20a%20folded%20sweater%20and%20a%20pair%20of%20shoes%20beside%20a%20tablet%20on%20a%20warm%20blush%20pink%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-buying&orientation=landscape",
    title: "Guía de compra de Basetao: de explorar al pago",
    cardDescription: "Una guía de compra de Basetao paso a paso — de elegir una categoría a revisar fotos de QC y pagar de forma segura.",
    metaDescription: "Una guía de compra de Basetao paso a paso sobre exploración, elección de vendedor, control de calidad y pago seguro.",
    eyebrow: "Guía de compra",
    intro: "Comprar en Basetao es sencillo cuando conoces el proceso. Esta guía te acompaña desde la primera búsqueda hasta un pago seguro y confiado.",
    sections: [
      {
        heading: "Empieza por la categoría correcta",
        paragraphs: [
          "Explora primero por categoría — Zapatos, Sudaderas, Camisetas, Chaquetas, Bolsos y más — para convertir una idea amplia en una lista concreta. Cada categoría abre una vista filtrada en el sitio principal.",
          "Acotar pronto ahorra tiempo. Cuanto más claro tengas lo que quieres, más fácil comparar las opciones que encajan.",
        ],
      },
      {
        heading: "Elige un vendedor y revisa la ficha",
        paragraphs: [
          "Lee la descripción completa, revisa las notas de talla y mira las fotos. Una buena ficha es específica sobre material, medidas y contenido.",
          "Compara varios vendedores en lugar de saltar al primero. Pequeñas diferencias de precio o detalle pueden importar.",
        ],
      },
      {
        heading: "Finaliza con QC y pago seguro",
        paragraphs: [
          "Pide fotos de control de calidad si está disponible y revísalas antes del envío. Luego completa el pago con un método reconocido y rastreable.",
          "Revisa el método de envío, el plazo estimado y el coste total antes de confirmar. Unos segundos más evitan la mayoría de problemas.",
        ],
      },
    ],
    comparisonTitle: "Proceso de compra de un vistazo",
    comparisonHeaders: ["Paso", "Qué haces", "Consejo clave"],
    comparisonRows: [
      ["Explorar", "Elegir categoría", "Partir de una idea clara"],
      ["Seleccionar", "Comparar vendedores", "Leer la descripción completa"],
      ["Revisar", "Comprobar tallas y fotos", "Pedir QC si es posible"],
      ["Pagar", "Pagar seguro", "Confirmar total y envío"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Cómo empiezo a comprar en Basetao?", a: "Elige una categoría, explora la lista filtrada en el sitio principal y compara algunas opciones." },
      { q: "¿Debo pedir control de calidad?", a: "Sí, sobre todo en ropa o artículos de valor. Ayuda a detectar problemas evidentes antes del envío." },
      { q: "¿Qué reviso antes de pagar?", a: "Confirma los detalles, el método de envío, el plazo estimado y el coste total antes de finalizar." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },

  "basetao-seller-guide": {
    slug: "basetao-seller-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20minimal%20storefront%20concept%20with%20a%20small%20wooden%20sign%2C%20a%20handshake%20figurine%20and%20product%20cards%20on%20a%20warm%20khaki%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-seller&orientation=landscape",
    title: "Guía de vendedores de Basetao: encontrar vendedores fiables",
    cardDescription: "Cómo identificar vendedores fiables de Basetao, leer fichas con ojo crítico y comparar antes de comprometerte.",
    metaDescription: "Aprende a encontrar vendedores fiables de Basetao, leer fichas con ojo crítico y comparar tus opciones antes de comprar.",
    eyebrow: "Vendedores",
    intro: "El vendedor que elijas tiene un gran impacto en lo que recibes. Esta guía explica cómo identificar vendedores fiables y leer fichas con ojo crítico.",
    sections: [
      {
        heading: "Qué hace fiable a un vendedor",
        paragraphs: [
          "Los vendedores fiables dan fichas completas con fotos claras, tallas precisas y descripciones de material honestas. Responden preguntas y ofrecen precios realistas.",
          "La coherencia es una señal fuerte. Un vendedor con fichas detalladas y correctas en varios productos suele ser más seguro que uno con descripciones vagas.",
        ],
      },
      {
        heading: "Leer una ficha con ojo crítico",
        paragraphs: [
          "Mira más allá de la primera foto y lee cada detalle. Revisa la guía de tallas, el material y si las fotos coinciden con la descripción.",
          "Desconfía de fichas con solo fotos de stock, sin medidas o que evitan responder preguntas básicas. La falta de detalle suele ser una señal.",
        ],
      },
      {
        heading: "Comparar vendedores antes de comprometerte",
        paragraphs: [
          "Compara varios vendedores para el mismo tipo de artículo. Mira el precio, el plazo, la calidad de la ficha y las reseñas disponibles.",
          "La opción más barata no siempre es la mejor. Un precio algo mayor con un vendedor más detallado y atento puede ahorrarte una decepción.",
        ],
      },
    ],
    comparisonTitle: "Lista de evaluación de vendedores",
    comparisonHeaders: ["Factor", "Señal fiable", "Señal de alerta"],
    comparisonRows: [
      ["Detalle de la ficha", "Fotos y tallas claras", "Descripción vaga"],
      ["Precio", "Realista y coherente", "Sospechosamente bajo"],
      ["Comunicación", "Responde preguntas", "Evita o ignora"],
      ["Fotos", "Coinciden con la descripción", "Solo fotos de stock"],
    ],
    faqTitle: "Preguntas frecuentes",
    faqs: [
      { q: "¿Cómo encuentro un vendedor fiable de Basetao?", a: "Busca fichas completas con fotos claras, tallas precisas y descripciones honestas. Compara antes de comprar." },
      { q: "¿Qué señales indican un mal vendedor?", a: "Fichas vagas, precios irreales, solo fotos de stock y vendedores que evitan responder." },
      { q: "¿El vendedor más barato es siempre el mejor?", a: "No. Un precio algo mayor con un vendedor más detallado suele llevar a una mejor compra." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipo editorial de Basetao Spreadsheet",
  },
};